import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import * as api from './seasonal-content.api'
import type {
  SeasonalDesignIndexKeyEntries,
  SeasonalDesignKeyEntries,
  SeasonalDesignRevisionsKeyEntries,
  SeasonalDesignScope,
  SeasonalDesignTarget,
  SeasonalOrganizationScope,
  SeasonalScheduleProfileTarget,
} from './seasonal-content.api'
import type { SeasonalDesignRecommendationInput } from './seasonal-content.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type SeasonalDesignIndexQueryKey = [AdminQueryRoot & SeasonalDesignIndexKeyEntries]
type SeasonalDesignQueryKey = [AdminQueryRoot & SeasonalDesignKeyEntries]
type SeasonalDesignRevisionsQueryKey = [AdminQueryRoot & SeasonalDesignRevisionsKeyEntries]
type SeasonalOrganizationQueryKey = [AdminQueryRoot & SeasonalOrganizationScope]

/** A page of a seasonal design's revision history. */
interface SeasonalDesignRevisionsScope extends SeasonalDesignScope {
  page?: number
  pageSize?: number
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const seasonalContentQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'seasonal-content' }, { organizationId }),
  ],
  designs: ({
    organizationId,
    filters,
  }: SeasonalDesignIndexKeyEntries): SeasonalDesignIndexQueryKey =>
    buildScopedAdminQueryKey(seasonalContentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'designs',
      filters,
    }),
  design: ({ organizationId, designId }: SeasonalDesignScope): SeasonalDesignQueryKey =>
    buildScopedAdminQueryKey(seasonalContentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'design',
      designId,
    }),
  revisions: ({
    organizationId,
    designId,
    page = 1,
    pageSize = 10,
  }: SeasonalDesignRevisionsScope): SeasonalDesignRevisionsQueryKey =>
    buildScopedAdminQueryKey(seasonalContentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'revisions',
      designId,
      page,
      pageSize,
    }),
  schedules: ({ organizationId }: SeasonalOrganizationScope): SeasonalOrganizationQueryKey =>
    buildScopedAdminQueryKey(seasonalContentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'schedules',
    }),
  status: ({ organizationId }: SeasonalOrganizationScope): SeasonalOrganizationQueryKey =>
    buildScopedAdminQueryKey(seasonalContentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'status',
    }),
}

export const seasonalContentQueries = {
  designs: ({ organizationId, filters }: SeasonalDesignIndexKeyEntries) =>
    indexTableQueryOptions({
      queryKey: seasonalContentQueryKeys.designs({ organizationId, filters }),
      queryFn: api.getSeasonalDesigns,
    }),
  design: ({ organizationId, designId }: SeasonalDesignScope) =>
    queryOptions({
      queryKey: seasonalContentQueryKeys.design({ organizationId, designId }),
      queryFn: api.getSeasonalDesign,
    }),
  revisions: ({
    organizationId,
    designId,
    page = 1,
    pageSize = 10,
  }: SeasonalDesignRevisionsScope) =>
    queryOptions({
      queryKey: seasonalContentQueryKeys.revisions({
        organizationId,
        designId,
        page,
        pageSize,
      }),
      queryFn: api.getSeasonalDesignRevisions,
    }),
  schedules: ({ organizationId }: SeasonalOrganizationScope) =>
    queryOptions({
      queryKey: seasonalContentQueryKeys.schedules({ organizationId }),
      queryFn: api.getSeasonalSchedules,
    }),
  status: ({ organizationId }: SeasonalOrganizationScope) =>
    queryOptions({
      queryKey: seasonalContentQueryKeys.status({ organizationId }),
      queryFn: api.getSeasonalStatus,
    }),
}

/** Seasonal content writes run in one organization and refresh all of its seasonal content. */
const useSeasonalMutation = <TVariables extends object, TData>(
  mutationFn: (variables: SeasonalOrganizationScope & TVariables) => Promise<TData>,
  organizationIdOverride?: OrganizationIdInput
) => {
  const organizationId = useRouteOrganizationId(organizationIdOverride)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: TVariables) => mutationFn({ organizationId, ...variables }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: seasonalContentQueryKeys.all({ organizationId }),
      }),
  })
}

export const useSeasonalDesigns = ({
  organizationId,
  filters,
  ...params
}: OrganizationScope &
  Pick<SeasonalDesignIndexKeyEntries, 'filters'> &
  QueryParams<ReturnType<typeof seasonalContentQueries.designs>> = {}) => {
  const id = useRouteOrganizationId(organizationId)
  return useQuery({ ...seasonalContentQueries.designs({ organizationId: id, filters }), ...params })
}

export const useSeasonalDesign = ({
  organizationId,
  designId,
  ...params
}: OrganizationScope &
  SeasonalDesignTarget &
  QueryParams<ReturnType<typeof seasonalContentQueries.design>>) => {
  const id = useRouteOrganizationId(organizationId)
  return useQuery({ ...seasonalContentQueries.design({ organizationId: id, designId }), ...params })
}

export const useSeasonalDesignRevisions = ({
  organizationId,
  designId,
  page = 1,
  pageSize = 10,
  ...params
}: OrganizationScope &
  Omit<SeasonalDesignRevisionsScope, 'organizationId'> &
  QueryParams<ReturnType<typeof seasonalContentQueries.revisions>>) => {
  const id = useRouteOrganizationId(organizationId)
  return useQuery({
    ...seasonalContentQueries.revisions({
      organizationId: id,
      designId,
      page,
      pageSize,
    }),
    ...params,
  })
}

export const useSeasonalSchedules = ({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof seasonalContentQueries.schedules>> = {}) => {
  const id = useRouteOrganizationId(organizationId)
  return useQuery({ ...seasonalContentQueries.schedules({ organizationId: id }), ...params })
}

export const useSeasonalStatus = ({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof seasonalContentQueries.status>> = {}) => {
  const id = useRouteOrganizationId(organizationId)
  return useQuery({ ...seasonalContentQueries.status({ organizationId: id }), ...params })
}

export const useSeasonalDesignImpactMutation = ({ organizationId }: OrganizationScope = {}) => {
  const id = useRouteOrganizationId(organizationId)
  return useMutation({
    mutationFn: ({ designId }: SeasonalDesignTarget) =>
      api.getSeasonalDesignImpact({ organizationId: id, designId }),
  })
}

export const useSeasonalScheduleImpactMutation = ({ organizationId }: OrganizationScope = {}) => {
  const id = useRouteOrganizationId(organizationId)
  return useMutation({
    mutationFn: ({ profileId }: SeasonalScheduleProfileTarget) =>
      api.getSeasonalScheduleImpact({ organizationId: id, profileId }),
  })
}

export const useCreateSeasonalDesignMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.createSeasonalDesign, organizationId)
export const useUpdateSeasonalDesignMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.updateSeasonalDesign, organizationId)
export const usePublishSeasonalDesignMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.publishSeasonalDesign, organizationId)
export const useArchiveSeasonalDesignMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.archiveSeasonalDesign, organizationId)
export const useRollbackSeasonalDesignMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.rollbackSeasonalDesign, organizationId)
export const useUpdateSeasonalScheduleMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.updateSeasonalSchedule, organizationId)
export const usePublishSeasonalScheduleMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.publishSeasonalSchedule, organizationId)
export const usePublishSeasonalSchedulesMutation = ({ organizationId }: OrganizationScope = {}) =>
  useSeasonalMutation(api.publishSeasonalSchedules, organizationId)
export const useSeasonalDesignRecommendationMutation = () => {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: ({ seasonalTagId, excludeDesignIds }: SeasonalDesignRecommendationInput) =>
      api.getSeasonalDesignRecommendation({ organizationId, seasonalTagId, excludeDesignIds }),
  })
}
